'use client';

import classNames from 'classnames';
import {formatNumber, useI18n} from '@udemy-v2/i18n';
import {StarRating} from '@udemy-v2/react-merchandising-components';
import {Tooltip} from '@udemy-v2/react-popup-components';

import styles from './rating-display.module.scss';
import {Icon} from '@udemy-v2/icons';

interface RatingDisplayProps {
    average: number;
    count: number;
    numeric?: boolean;
    isAggregated?: boolean;
}

/**
 * Displays the course rating with stars and count
 */
export const RatingDisplay = ({average, count, numeric, isAggregated}: RatingDisplayProps) => {
    const {gettext, interpolate} = useI18n();

    // Determine rating text based on count
    const ratingText = count === 1 ? gettext('rating') : gettext('ratings');

    return (
        <div className={styles['rating-container']}>
            <span className={styles['rating-display-container']}>
                <StarRating
                    rating={average}
                    showNumber={true}
                    numeric={numeric}
                    borderedStarClassName={styles['star-rating-icon-color-bordered']}
                    filledStarClassName={styles['star-rating-icon-color']}
                />
                {isAggregated && (
                    <Tooltip
                        placement="bottom"
                        renderTrigger={(triggerProps) => (
                            <span
                                {...triggerProps}
                                className={styles['rating-display-container-tooltip']}
                            >
                                <Icon glyph="info-outline" label={false} />
                            </span>
                        )}
                    >
                        {gettext(
                            'This rating represents a weighted average of learner ratings across courses belonging to this variant group. Individual course ratings may vary.',
                        )}
                    </Tooltip>
                )}
            </span>
            {count > 0 && (
                <a href="#reviews" className={classNames('ud-text-sm', styles['rating-count'])}>
                    {interpolate(
                        gettext('(%(count)s %(rating_text)s)'),
                        {
                            count: formatNumber(count),
                            rating_text: ratingText,
                        },
                        true,
                    )}
                </a>
            )}
        </div>
    );
};
